import { FC } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Tabs,
  Card,
  Spin,
  Empty,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  StarOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { useGetStaffById } from "~/services/operation";
import { formatStaffName } from "~/utils/format-staff-name";
import { capitalize } from "~/utils";
import dayjs from "dayjs";
import { StarRating } from "~/components/common/star-rating";

interface Props {
  staffId: string | null;
  visible: boolean;
  onClose: () => void;
}

export const StaffProfileView: FC<Props> = ({ staffId, visible, onClose }) => {
  const { data: staff, isLoading } = useGetStaffById(
    staffId || "",
    !!staffId && visible
  );

  if (!staffId) return null;

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>Staff Profile</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
    >
      <Spin spinning={isLoading}>
        {staff ? (
          <Tabs
            defaultActiveKey="profile"
            items={[
              {
                key: "profile",
                label: (
                  <span>
                    <UserOutlined />
                    Profile Information
                  </span>
                ),
                children: (
                  <Card>
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={{ fontWeight: 500, width: "200px" }}
                    >
                      <Descriptions.Item
                        label={
                          <Space>
                            <UserOutlined />
                            Name
                          </Space>
                        }
                      >
                        {formatStaffName(staff)}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <UserOutlined />
                            Username
                          </Space>
                        }
                      >
                        {staff.user?.username || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <MailOutlined />
                            Email
                          </Space>
                        }
                      >
                        {staff.user?.email || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <PhoneOutlined />
                            Phone
                          </Space>
                        }
                      >
                        {staff.user?.phoneNumber || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <TrophyOutlined />
                            Position
                          </Space>
                        }
                      >
                        {staff.position || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item label="Role">
                        <Space>
                          {staff.lead && <Tag color="purple">Lead</Tag>}
                          {staff.driver && <Tag color="blue">Driver</Tag>}
                          {!staff.lead && !staff.driver && <Tag>Staff</Tag>}
                        </Space>
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <StarOutlined />
                            Level
                          </Space>
                        }
                      >
                        {(() => {
                          const installation = staff.installation ?? 0;
                          const packing = staff.packing ?? 0;
                          const handling = staff.handling ?? 0;
                          const clientRelations = staff.clientRelations ?? 0;
                          const teamwork = staff.teamwork ?? 0;
                          const overallLevel =
                            (installation +
                              packing +
                              handling +
                              clientRelations +
                              teamwork) /
                            5;

                          const hasSkills =
                            installation > 0 ||
                            packing > 0 ||
                            handling > 0 ||
                            clientRelations > 0 ||
                            teamwork > 0;

                          if (!hasSkills) {
                            return (
                              <span style={{ color: "#999" }}>
                                No level set yet
                              </span>
                            );
                          }

                          return (
                            <div>
                              <div style={{ marginBottom: "12px" }}>
                                <Space>
                                  <span style={{ fontWeight: 600 }}>
                                    Overall: {overallLevel.toFixed(1)}
                                  </span>
                                  <StarRating rating={overallLevel} />
                                </Space>
                              </div>
                              <div style={{ fontSize: "12px" }}>
                                <Space direction="vertical" size="small">
                                  {installation > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Installation:
                                      </span>
                                      <StarRating rating={installation} />
                                      <span>({installation})</span>
                                    </Space>
                                  )}
                                  {packing > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Packing:
                                      </span>
                                      <StarRating rating={packing} />
                                      <span>({packing})</span>
                                    </Space>
                                  )}
                                  {handling > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Handling:
                                      </span>
                                      <StarRating rating={handling} />
                                      <span>({handling})</span>
                                    </Space>
                                  )}
                                  {clientRelations > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Client Relations:
                                      </span>
                                      <StarRating rating={clientRelations} />
                                      <span>({clientRelations})</span>
                                    </Space>
                                  )}
                                  {teamwork > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Teamwork:
                                      </span>
                                      <StarRating rating={teamwork} />
                                      <span>({teamwork})</span>
                                    </Space>
                                  )}
                                </Space>
                              </div>
                            </div>
                          );
                        })()}
                      </Descriptions.Item>

                      <Descriptions.Item label="Skills & Certifications">
                        {staff.skills && staff.skills.length > 0 ? (
                          <Space wrap>
                            {staff.skills.map((skill, index) => (
                              <Tag
                                key={index}
                                color={
                                  skill.includes("Forklift") ||
                                  skill.includes("OSHA")
                                    ? "green"
                                    : "blue"
                                }
                              >
                                {skill}
                              </Tag>
                            ))}
                          </Space>
                        ) : (
                          <span style={{ color: "#999" }}>No skills listed</span>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item label="Status">
                        {staff.status ? (
                          <Tag
                            color={
                              staff.status === "ACTIVE"
                                ? "green"
                                : staff.status === "HIATUS"
                                ? "orange"
                                : "red"
                            }
                          >
                            {capitalize(staff.status)}
                          </Tag>
                        ) : (
                          <Tag color="green">Active</Tag>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <CalendarOutlined />
                            Added Date
                          </Space>
                        }
                      >
                        {staff.createdAt
                          ? dayjs(staff.createdAt).format("MMMM D, YYYY")
                          : "-"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
              {
                key: "service-history",
                label: (
                  <span>
                    <CalendarOutlined />
                    Service Ticket History
                  </span>
                ),
                children: (
                  <Card>
                    <Empty
                      description="Service ticket history coming soon"
                      style={{ padding: "40px 0" }}
                    />
                  </Card>
                ),
              },
            ]}
          />
        ) : (
          <Empty description="Staff not found" />
        )}
      </Spin>
    </Modal>
  );
};
